import React, { useState, useEffect } from "react";
import {
  MdPeople,
  MdSchool,
  MdBook,
  MdAccountBalance,
  MdAttachMoney,
  MdCalendarToday,
  MdOutlineTrendingUp,
  MdOutlineTrendingDown,
  MdGroups,
  MdLibraryBooks,
  MdArrowUpward,
  
} from "react-icons/md";
import Card from "components/card";
import Chart from "react-apexcharts";

// Services
import MembreServiceInstance from "service/membre.service";
import InscriptionServiceInstance from "service/inscription.service";
import SosialyServiceInstance from "service/sosialy.service";
import UserServiceInstance from "service/users.service";
import MemoireServiceInstance from "service/memoire.service";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    membres: {
      total: 0,
      nouveaux: 0,
      actifs: 0,
      parPromo: [],
    },
    inscriptions: {
      total: 0,
      parNiveau: [],
      parParcours: [],
      parAnnee: [],
      recent: [],
    },
    memoires: {
      total: 0,
      parFiliere: [],
      parAnnee: [],
      recent: [],
    },
    sosialy: {
      total: 0,
      montantTotal: 0,
      moisEnCours: 0,
      tendance: 0,
      historique: [],
    },
    users: {
      total: 0,
      actifs: 0,
      inactifs: 0,
      parRole: [],
    },
    recentActivity: [],
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Pour un dashboard réel, vous devriez avoir un endpoint spécifique
      // qui retourne toutes les données nécessaires en une seule requête
      // Ici, on simule avec plusieurs appels API

      const [
        membresResult,
        inscriptionsResult,
        sosialyResult,
        usersResult,
        memoiresResult,
      ] = await Promise.all([
        MembreServiceInstance.getAll(),
        InscriptionServiceInstance.getAll(),
        SosialyServiceInstance.getAll(),
        UserServiceInstance.getAll(),
        MemoireServiceInstance.getAll(),
      ]);

      // Traitement des données pour le dashboard
      const membresStats = processMembresData(membresResult || []);
      const inscriptionsStats = processInscriptionsData(
        inscriptionsResult || []
      );
      const sosialyStats = processSosialyData(sosialyResult || []);
      const usersStats = processUsersData(usersResult || []);
      const memoiresStats = processMemoiresData(memoiresResult || []);

      // Génération de données récentes d'activité (exemple)
      const recentActivity = generateRecentActivity([
        ...(inscriptionsResult?.slice(0, 3) || []),
        ...(sosialyResult?.slice(0, 3) || []),
        ...(memoiresResult?.slice(0, 3) || []),
      ]);

      setStats({
        membres: membresStats,
        inscriptions: inscriptionsStats,
        sosialy: sosialyStats,
        users: usersStats,
        memoires: memoiresStats,
        recentActivity,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError("Impossible de charger les données du tableau de bord");
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour calculer les statistiques des membres
  const processMembresData = (membres) => {
    const now = new Date();
    const oneMonthAgo = new Date(now.setMonth(now.getMonth() - 1));

    // Compter les nouveaux membres (inscrits le mois dernier)
    const nouveaux = membres.filter((membre) => {
      const membreDate = new Date(membre.date_creation || membre.date_inscrit);
      return membreDate >= oneMonthAgo;
    }).length;

    // Compter les membres par promotion
    const parPromo = {};
    membres.forEach((membre) => {
      if (membre.promotion) {
        const promoName = membre.promotion.name_prom;
        parPromo[promoName] = (parPromo[promoName] || 0) + 1;
      }
    });

    const parPromoArray = Object.entries(parPromo).map(([name, count]) => ({
      name,
      count,
    }));

    return {
      total: membres.length,
      nouveaux,
      actifs: membres.filter((m) => m.is_active !== false).length,
      parPromo: parPromoArray,
    };
  };

  // Fonction pour calculer les statistiques des inscriptions
  const processInscriptionsData = (inscriptions) => {
    // Compter par niveau
    const parNiveau = {};
    // Compter par parcours
    const parParcours = {};
    // Compter par année universitaire
    const parAnnee = {};

    inscriptions.forEach((inscription) => {
      if (inscription.niveau) {
        parNiveau[inscription.niveau] =
          (parNiveau[inscription.niveau] || 0) + 1;
      }

      if (inscription.name_parcours) {
        parParcours[inscription.name_parcours] =
          (parParcours[inscription.name_parcours] || 0) + 1;
      }

      if (inscription.dateuniv) {
        parAnnee[inscription.dateuniv] =
          (parAnnee[inscription.dateuniv] || 0) + 1;
      }
    });

    const parNiveauArray = Object.entries(parNiveau).map(([name, count]) => ({
      name,
      count,
    }));
    const parParcoursArray = Object.entries(parParcours).map(
      ([name, count]) => ({ name, count })
    );
    const parAnneeArray = Object.entries(parAnnee).map(([name, count]) => ({
      name,
      count,
    }));

    // Trier les inscriptions récentes
    const recent = [...inscriptions]
      .sort((a, b) => new Date(b.date_inscrit) - new Date(a.date_inscrit))
      .slice(0, 5);

    return {
      total: inscriptions.length,
      parNiveau: parNiveauArray,
      parParcours: parParcoursArray,
      parAnnee: parAnneeArray,
      recent,
    };
  };

  // Fonction pour calculer les statistiques des cotisations
  const processSosialyData = (sosialy) => {
    const montantTotal = sosialy.reduce(
      (acc, item) => acc + parseFloat(item.montant_sosialy || 0),
      0
    );

    // Calculer les montants par mois
    const historiqueParMois = {};

    sosialy.forEach((item) => {
      const moisAnnee = `${item.mois_sosialy} ${item.annee_sosialy}`;
      historiqueParMois[moisAnnee] =
        (historiqueParMois[moisAnnee] || 0) +
        parseFloat(item.montant_sosialy || 0);
    });

    // Convertir en tableau pour le graphique
    const historique = Object.entries(historiqueParMois).map(
      ([mois, montant]) => ({
        mois,
        montant,
      })
    );

    // Trier par date
    historique.sort((a, b) => {
      const [moisA, anneeA] = a.mois.split(" ");
      const [moisB, anneeB] = b.mois.split(" ");

      if (anneeA !== anneeB) return anneeA - anneeB;

      const moisOrder = [
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre",
      ];

      return moisOrder.indexOf(moisA) - moisOrder.indexOf(moisB);
    });

    // Calculer la tendance (différence entre les 2 derniers mois)
    let tendance = 0;
    if (historique.length >= 2) {
      const dernierMontant = historique[historique.length - 1].montant;
      const avantDernierMontant = historique[historique.length - 2].montant;
      tendance = dernierMontant - avantDernierMontant;
    }

    // Montant du mois en cours
    const maintenant = new Date();
    const moisActuels = [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ];
    const moisEnCours = moisActuels[maintenant.getMonth()];
    const anneeEnCours = maintenant.getFullYear().toString();
    const moisAnneeEnCours = `${moisEnCours} ${anneeEnCours}`;

    const montantMoisEnCours = historiqueParMois[moisAnneeEnCours] || 0;

    return {
      total: sosialy.length,
      montantTotal,
      moisEnCours: montantMoisEnCours,
      tendance,
      historique,
    };
  };

  // Fonction pour calculer les statistiques des utilisateurs
  const processUsersData = (users) => {
    const actifs = users.filter((u) => u.isActive).length;
    const inactifs = users.length - actifs;

    // Distribution par rôle
    const parRole = {};
    users.forEach((user) => {
      if (user.role && user.role.role_name) {
        parRole[user.role.role_name] = (parRole[user.role.role_name] || 0) + 1;
      }
    });

    const parRoleArray = Object.entries(parRole).map(([name, count]) => ({
      name,
      count,
    }));

    return {
      total: users.length,
      actifs,
      inactifs,
      parRole: parRoleArray,
    };
  };

  // Fonction pour calculer les statistiques des mémoires
  const processMemoiresData = (memoires) => {
    // Compter par filière
    const parFiliere = {};
    // Compter par année
    const parAnnee = {};

    memoires.forEach((memoire) => {
      if (memoire.memoire_filiere) {
        parFiliere[memoire.memoire_filiere] =
          (parFiliere[memoire.memoire_filiere] || 0) + 1;
      }

      if (memoire.memoire_annee) {
        parAnnee[memoire.memoire_annee] =
          (parAnnee[memoire.memoire_annee] || 0) + 1;
      }
    });

    const parFiliereArray = Object.entries(parFiliere).map(([name, count]) => ({
      name,
      count,
    }));
    const parAnneeArray = Object.entries(parAnnee).map(([name, count]) => ({
      name,
      count,
    }));

    // Trier les mémoires récentes
    const recent = [...memoires]
      .sort(
        (a, b) =>
          new Date(b.date_creation || b.memoire_date) -
          new Date(a.date_creation || a.memoire_date)
      )
      .slice(0, 5);

    return {
      total: memoires.length,
      parFiliere: parFiliereArray,
      parAnnee: parAnneeArray,
      recent,
    };
  };

  // Fonction pour générer les activités récentes
  const generateRecentActivity = (data) => {
    const activities = [];

    data.forEach((item) => {
      if (item.nom_prenom_membre && item.date_inscrit) {
        // C'est une inscription
        activities.push({
          type: "inscription",
          titre: `Inscription de ${item.nom_prenom_membre}`,
          description: `${item.niveau || ""} - ${item.name_parcours || ""}`,
          date: item.date_inscrit,
          icon: <MdSchool className="h-5 w-5 text-purple-600" />,
        });
      } else if (item.memoire_theme && item.memoire_date) {
        // C'est un mémoire
        activities.push({
          type: "memoire",
          titre: `Nouveau mémoire: ${item.memoire_theme}`,
          description: `Par ${item.memoire_etudiant || "Étudiant"}`,
          date: item.memoire_date,
          icon: <MdBook className="h-5 w-5 text-indigo-600" />,
        });
      } else if (item.montant_sosialy && item.date_sosialy) {
        // C'est une cotisation
        activities.push({
          type: "sosialy",
          titre: `Cotisation: ${item.objet_sosialy || "Paiement"}`,
          description: `${
            item.membre?.nom_prenom_membre || "Membre"
          } - ${formatCurrency(item.montant_sosialy)}`,
          date: item.date_sosialy,
          icon: <MdAttachMoney className="h-5 w-5 text-green-600" />,
        });
      }
    });

    // Trier par date décroissante
    activities.sort((a, b) => new Date(b.date) - new Date(a.date));

    return activities.slice(0, 10); // Limiter aux 10 dernières activités
  };

  // Formatage pour la monnaie (MGA)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-MG", {
      style: "currency",
      currency: "MGA",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Configuration des graphiques pour ApexCharts
  const inscriptionsNiveauOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        distributed: true,
        borderRadius: 4,
        horizontal: false,
        columnWidth: "60%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    xaxis: {
      categories: stats.inscriptions.parNiveau.map((n) => n.name),
      labels: {
        style: {
          fontSize: "12px",
          fontFamily: "inherit",
        },
      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return Math.round(val);
        },
      },
    },
    colors: ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"],
    title: {
      text: "Inscriptions par niveau",
      align: "left",
      style: {
        fontSize: "14px",
        fontFamily: "inherit",
        fontWeight: 500,
      },
    },
  };

  const inscriptionsNiveauSeries = [
    {
      name: "Nombre d'inscriptions",
      data: stats.inscriptions.parNiveau.map((n) => n.count),
    },
  ];

  const cotisationsOptions = {
    chart: {
      type: "area",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.3,
        stops: [0, 90, 100],
      },
    },
    xaxis: {
      categories: stats.sosialy.historique.map((h) => h.mois),
      labels: {
        style: {
          fontSize: "12px",
          fontFamily: "inherit",
        },
      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return val.toLocaleString() + " Ar";
        },
      },
    },
    colors: ["#10b981"],
    title: {
      text: "Évolution des cotisations",
      align: "left",
      style: {
        fontSize: "14px",
        fontFamily: "inherit",
        fontWeight: 500,
      },
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return formatCurrency(val);
        },
      },
    },
  };

  const cotisationsSeries = [
    {
      name: "Montant des cotisations",
      data: stats.sosialy.historique.map((h) => h.montant),
    },
  ];

  const usersRoleOptions = {
    chart: {
      type: "donut",
    },
    labels: stats.users.parRole.map((r) => r.name),
    colors: ["#6366f1", "#10b981", "#f59e0b", "#06b6d4", "#8b5cf6"],
    legend: {
      position: "bottom",
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: "100%",
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total",
              formatter: function () {
                return stats.users.total;
              },
            },
          },
        },
      },
    },
    title: {
      text: "Répartition par rôle",
      align: "left",
      style: {
        fontSize: "14px",
        fontFamily: "inherit",
        fontWeight: 500,
      },
    },
  };

  const usersRoleSeries = stats.users.parRole.map((r) => r.count);

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-8 md:px-8">
      <div className="mx-auto">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800">Tableau de bord</h1>
          <p className="mt-1 text-gray-600">
            Aperçu général -{" "}
            {new Date().toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </header>

        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-indigo-500"></div>
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">
            {error}
          </div>
        ) : (
          <>
            {/* Statistiques générales */}
            <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {/* Membres */}
              <Card extra="!p-4 flex flex-col">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total des membres
                    </p>
                    <h3 className="mt-1 text-2xl font-bold text-gray-800">
                      {stats.membres.total}
                    </h3>
                  </div>
                  <div className="rounded-full bg-indigo-50 p-3 text-indigo-500">
                    <MdPeople className="h-6 w-6" />
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center">
                    <MdArrowUpward
                      className={`h-4 w-4 ${
                        stats.membres.nouveaux > 0
                          ? "text-green-500"
                          : "text-gray-400"
                      }`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        stats.membres.nouveaux > 0
                          ? "text-green-500"
                          : "text-gray-500"
                      } ml-1`}
                    >
                      {stats.membres.nouveaux} nouveau
                      {stats.membres.nouveaux > 1 ? "x" : ""} ce mois-ci
                    </span>
                  </div>
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-gray-600">
                      Actifs: {stats.membres.actifs}
                    </span>
                    <span className="font-medium text-indigo-600">
                      {Math.round(
                        (stats.membres.actifs / stats.membres.total) * 100
                      )}
                      %
                    </span>
                  </div>
                </div>
              </Card>

              {/* Inscriptions */}
              <Card extra="!p-4 flex flex-col">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total des inscriptions
                    </p>
                    <h3 className="mt-1 text-2xl font-bold text-gray-800">
                      {stats.inscriptions.total}
                    </h3>
                  </div>
                  <div className="rounded-full bg-purple-50 p-3 text-purple-500">
                    <MdSchool className="h-6 w-6" />
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex justify-between">
                    {stats.inscriptions.parNiveau
                      .slice(0, 3)
                      .map((niveau, index) => (
                        <div
                          key={index}
                          className="rounded bg-purple-50 px-2 py-1 text-xs"
                        >
                          <span className="font-medium text-purple-700">
                            {niveau.name}
                          </span>
                        </div>
                      ))}
                  </div>
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-gray-600">Parcours:</span>
                    <span className="font-medium text-purple-600">
                      {stats.inscriptions.parParcours.length}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Sosialy */}
              <Card extra="!p-4 flex flex-col">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total des cotisations
                    </p>
                    <h3 className="mt-1 text-2xl font-bold text-gray-800">
                      {formatCurrency(stats.sosialy.montantTotal)}
                    </h3>
                  </div>
                  <div className="rounded-full bg-green-50 p-3 text-green-500">
                    <MdAttachMoney className="h-6 w-6" />
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center">
                    {stats.sosialy.tendance > 0 ? (
                      <MdOutlineTrendingUp className="h-4 w-4 text-green-500" />
                    ) : (
                      <MdOutlineTrendingDown className="h-4 w-4 text-amber-500" />
                    )}
                    <span
                      className={`text-sm font-medium ${
                        stats.sosialy.tendance > 0
                          ? "text-green-500"
                          : "text-amber-500"
                      } ml-1`}
                    >
                      {stats.sosialy.tendance > 0 ? "+" : ""}
                      {formatCurrency(stats.sosialy.tendance)} par rapport au
                      mois précédent
                    </span>
                  </div>
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-gray-600">Mois en cours:</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(stats.sosialy.moisEnCours)}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Mémoires */}
              <Card extra="!p-4 flex flex-col">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total des mémoires
                    </p>
                    <h3 className="mt-1 text-2xl font-bold text-gray-800">
                      {stats.memoires.total}
                    </h3>
                  </div>
                  <div className="rounded-full bg-blue-50 p-3 text-blue-500">
                    <MdLibraryBooks className="h-6 w-6" />
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <MdGroups className="h-4 w-4 text-blue-500" />
                      <span className="ml-1 text-sm text-gray-600">
                        {stats.memoires.parFiliere.length} filière
                        {stats.memoires.parFiliere.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <MdCalendarToday className="h-4 w-4 text-blue-500" />
                      <span className="ml-1 text-sm text-gray-600">
                        {stats.memoires.parAnnee.length} année
                        {stats.memoires.parAnnee.length > 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-gray-600">Téléchargements:</span>
                    <span className="font-medium text-blue-600">154</span>
                  </div>
                </div>
              </Card>
            </div>

            {/* Graphiques et statistiques détaillées */}
            <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Évolution des cotisations */}
              <Card extra="!p-4">
                <div className="mb-2">
                  <h4 className="text-lg font-semibold text-gray-800">
                    Évolution des cotisations
                  </h4>
                </div>
                <div className="h-80">
                  <Chart
                    options={cotisationsOptions}
                    series={cotisationsSeries}
                    type="area"
                    height="100%"
                  />
                </div>
              </Card>

              {/* Inscriptions par niveau */}
              <Card extra="!p-4">
                <div className="mb-2">
                  <h4 className="text-lg font-semibold text-gray-800">
                    Inscriptions par niveau
                  </h4>
                </div>
                <div className="h-80">
                  <Chart
                    options={inscriptionsNiveauOptions}
                    series={inscriptionsNiveauSeries}
                    type="bar"
                    height="100%"
                  />
                </div>
              </Card>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Répartition des utilisateurs par rôle */}
              <Card extra="!p-4">
                <div className="mb-3">
                  <h4 className="text-lg font-semibold text-gray-800">
                    Utilisateurs par rôle
                  </h4>
                  <p className="text-sm text-gray-500">
                    Total: {stats.users.total} utilisateurs
                  </p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="h-64 w-full">
                    <Chart
                      options={usersRoleOptions}
                      series={usersRoleSeries}
                      type="donut"
                      height="100%"
                    />
                  </div>
                  <div className="mt-4 flex gap-4">
                    <div className="text-center">
                      <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-600">
                        <MdAccountBalance className="h-5 w-5" />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Actifs</p>
                      <p className="text-lg font-semibold text-green-600">
                        {stats.users.actifs}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600">
                        <MdAccountBalance className="h-5 w-5" />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Inactifs</p>
                      <p className="text-lg font-semibold text-red-600">
                        {stats.users.inactifs}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Activité récente */}
              <Card extra="!p-4 lg:col-span-2">
                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-gray-800">
                    Activité récente
                  </h4>
                  <p className="text-sm text-gray-500">
                    Dernières actions effectuées
                  </p>
                </div>
                <div className="max-h-[320px] space-y-4 overflow-y-auto pr-2">
                  {stats.recentActivity.map((activity, index) => (
                    <div key={index} className="flex space-x-3">
                      <div className="flex-shrink-0">
                        <div className="rounded-full bg-gray-100 p-2">
                          {activity.icon}
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800">
                          {activity.titre}
                        </p>
                        <p className="text-sm text-gray-500">
                          {activity.description}
                        </p>
                      </div>
                      <div className="flex-shrink-0 whitespace-nowrap text-sm text-gray-500">
                        {new Date(activity.date).toLocaleDateString("fr-FR", {
                          day: "numeric",
                          month: "short",
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
